import { differenceInMinutes, isAfter } from "date-fns";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import * as Yup from "yup";

import { isCutoffClosingTooSoon } from "../../../../../../utils/acceptance-cutoff";
import { calculateShiftHoursAndMinutes } from "../../../../../../utils/helpers";
import { getShiftPurposeDetailField } from "../../../../../../utils/shift-purpose-detail-field";
import { FilterContext } from "../../../../../filters/filter-context";
import { fetchMaxWorkersByLocationJob } from "../../../../../jobs/store/jobs-slice";
import useCanDirectAssign from "../../../../controllers/use-can-direct-assign";
import useSchedulerData from "../../../../controllers/use-scheduler-data";
import { Location, SchedulerWorkCenter } from "../../../../store/types";
import {
	configureDatesOfTheWeek,
	getInitValues,
	getYupInitObject,
	resolveDefaultWorkcenter,
} from "../utils";

type UseNewShiftDialogProps = {
  draftShiftDate: Date | { startDate: Date, endDate: Date };
  // Post Coverage: also fetch the eligible Workers so the supervisor can pick
  // who covers, where Direct Worker Assignment is on for the location
  includeEligibleWorkers?: boolean;
};

type JobRole = any;

type FormValues = {
	startTime: string;
	endTime: string;
	numberOfWorkersNeeded: string;
};

const useNewShiftDialog = ({
	draftShiftDate,
	includeEligibleWorkers = false,
}: UseNewShiftDialogProps) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { canDirectAssign } = useCanDirectAssign();

	const [filteredJobs, setFilteredJobs] = useState<JobRole[]>([]);
	const [currentLocationId, setCurrentLocationId] = useState("");
	const [lastSelectedJobId, setLastSelectedJobId] = useState("");
	// const [lastSelectedHexColor, setLastSelectedHexColor] = useState("");
	// const [lastSelectedWorkcenterId, setLastSelectedWorkcenterId] = useState("");
	const [shiftType, setShiftType] = useState("jobs");
	const {
		allJobRoles,
		filterJobRoles,
		filterAllWorkcenters,
		filterAllLocations,
		fetchFilterAndShiftDataStatus,
		defaultDurationHrs,
		weekStartDay,
		filterAllSkills,
		allSkillLevels,
		defaultShiftsSkillLevel,
		minShiftHourDuration,
		maxShiftHourDuration,
		shiftPurposes,
		isShiftPurposeEnabled,
	} = useSchedulerData();
	// The dialog is also rendered outside the scheduler's FilterProvider, so
	// the applied filters are read defensively.
	const appliedFilters = useContext(FilterContext)?.appliedFilters;

	const initialValues = useMemo(
		() =>
			getInitValues({
				draftShiftDate,
				defaultDurationHrs,
				shiftPurposeId: 0,
				weekStartDay,
			}),
		[draftShiftDate, defaultDurationHrs, weekStartDay]
	);

	const schema = useMemo(() => {
		return Yup.object({
			...getYupInitObject(t),
			shiftPurposeDetail: Yup.string().test(
				"shift-purpose-detail",
				function (value) {
					const { shiftPurposeId } = this.parent;
					const detailField = getShiftPurposeDetailField(
						shiftPurposes,
						shiftPurposeId
					);
					if (!isShiftPurposeEnabled || !detailField) return true;
					const detail = value?.trim() ?? "";
					if (detailField.isRequired && detail.length === 0) {
						return this.createError({
							message: t("scheduler_purpose-detail-required", {
								label: detailField.label,
							}),
						});
					}
					if (detail.length > detailField.maxLength) {
						return this.createError({
							message: t("scheduler_purpose-detail-max-length", {
								count: detailField.maxLength,
							}),
						});
					}
					return true;
				}
			),
			endTime: Yup.string()
				.required(t("scheduler_end-time-required"))
				.test(
					"is-greater",
					t("scheduler_end-time-greater-start"),
					function (value: string | undefined) {
						const { startTime } = this.parent || {};
						return value ? isAfter(new Date(value), new Date(startTime)) : !!value;
					}
				)
				.test(
					"min-shift-duration",
					function (value) {
						const { startTime, locationId } = this.parent;
						if (!startTime || !value || !locationId) return true;

						const selectedLocation = filterAllLocations?.find(
							(l: any) => l.id === Number(locationId)
						);

						const minHours =
							(selectedLocation as any)?.minShiftDuration ??
							minShiftHourDuration;

						const shiftDuration =
							differenceInMinutes(new Date(value), new Date(startTime)) / 60;

						return (
							shiftDuration >= minHours ||
							this.createError({
								message: t("scheduler_min-shift-duration", {
									count: minHours,
									unit:
										minHours > 1
											? t("scheduler_hours-unit")
											: t("scheduler_hour-unit"),
								}),
							})
						);
					}
				)
				.test(
					"max-shift-duration",
					function (value) {
						const { startTime, locationId } = this.parent;
						if (!startTime || !value || !locationId) return true;

						const selectedLocation = filterAllLocations?.find(
							(l: any) => l.id === Number(locationId)
						);

						const maxHours =
							(selectedLocation as any)?.maxShiftDuration ??
							maxShiftHourDuration;

						const shiftDuration =
							differenceInMinutes(new Date(value), new Date(startTime)) / 60;

						return (
							shiftDuration <= maxHours ||
							this.createError({
								message: t("scheduler_max-shift-duration", {
									count: maxHours,
									unit:
										maxHours > 1
											? t("scheduler_hours-unit")
											: t("scheduler_hour-unit"),
								}),
							})
						);
					}
				),
			allowPartialSignups: Yup.boolean().test(
				"min-partial-shift-duration",
				t("scheduler_partial-min-shift-max-total"),
				function (value) {
					if (!value) return true;
					const { startTime, endTime, locationId } = this.parent;
					if (!startTime || !endTime || !locationId) return true;

					const selectedLocation = filterAllLocations?.find(
						(l: any) => l.id === Number(locationId)
					);
					const minHours = selectedLocation?.partialShiftMinHours ?? 0;

					const shiftDuration =
						differenceInMinutes(new Date(endTime), new Date(startTime)) / 60;

					return shiftDuration >= minHours;
				}
			),
			isShiftAcceptanceCutoffEnabled: Yup.boolean(),
			minHoursBeforeShiftStartToAccept: Yup.number().when(
				"isShiftAcceptanceCutoffEnabled",
				{
					is: true,
					then: Yup.number()
						.required(t("scheduler_acceptance-cutoff-required"))
						.min(0.5, t("scheduler_acceptance-cutoff-range"))
					.max(168, t("scheduler_acceptance-cutoff-range"))
					.test(
						"cutoff-leaves-time-to-accept",
						t("scheduler_acceptance-cutoff-too-soon"),
						function (value) {
							const { startTime, isShiftAcceptanceCutoffEnabled } = this.parent;
							if (!isShiftAcceptanceCutoffEnabled || !startTime || !value) {
								return true;
							}
							return !isCutoffClosingTooSoon(startTime, value);
						}
					),
				}
			),
			isFlexiSignupEnabled: Yup.boolean().test(
				"min-flexi-shift-duration",
				function (value) {
					if (!value) return true;
					const { startTime, endTime, locationId } = this.parent;
					if (!startTime || !endTime || !locationId) return true;

					const shiftDuration =
						differenceInMinutes(new Date(endTime), new Date(startTime)) / 60;

					const selectedLocation = filterAllLocations?.find(
						(l: any) => l.id === Number(locationId)
					);
					const minHours = selectedLocation?.flexiShiftMinHours ?? 1;

					return (
						shiftDuration >= minHours ||
						this.createError({
							message: t("scheduler_min-flexi-shift-duration", {
								count: minHours,
								unit:
									minHours > 1
										? t("scheduler_hours-unit")
										: t("scheduler_hour-unit"),
							}),
						})
					);
				}
			),
			minFlexiSignupHours: Yup.number().when("isFlexiSignupEnabled", {
				is: true,
				then: Yup.number()
					.required(t("scheduler_min-signup-duration-required"))
					.integer(t("scheduler_must-be-integer"))
					.min(1, ({ min }) =>
						t("scheduler_min-signup-duration-hours", {
							count: min,
							unit:
								min > 1 ? t("scheduler_hours-unit") : t("scheduler_hour-unit"),
						})
					)
					.test(
						"max-shift-duration",
						t("scheduler_min-signup-less-than-total"),
						function (value) {
							const { startTime, endTime } = this.parent;
							if (!startTime || !endTime || !value) return true;
							const shiftDurationHours =
								differenceInMinutes(new Date(endTime), new Date(startTime)) /
								60;
							return value < shiftDurationHours;
						}
					),
			}),
		});
	}, [
		filterAllLocations,
		minShiftHourDuration,
		maxShiftHourDuration,
		shiftPurposes,
		isShiftPurposeEnabled,
		t,
	]);

	const [totalHours, setTotalHours] = useState(0);
	const [totalMins, setTotalMins] = useState(0);
	const [initialShiftDateTimeValues, setInitialShiftDateTimeValue] =
		useState(initialValues);
	const [selectedDatesOfTheWeek, setSelectedDatesOfTheWeek] = useState(() =>
		configureDatesOfTheWeek(new Date(initialValues.startTime), weekStartDay)
	);

	useEffect(() => {
		const initValues = getInitValues({
			draftShiftDate,
			defaultDurationHrs,
			shiftPurposeId: 0,
			weekStartDay,
		});
		setInitialShiftDateTimeValue(initValues);
		setSelectedDatesOfTheWeek(
			configureDatesOfTheWeek(new Date(initValues.startTime), weekStartDay)
		);
	}, [draftShiftDate, defaultDurationHrs, weekStartDay]);

	const filterAndAutoSelectJobs = useCallback(
		(workcenterId: string, setFieldvalue?: any) => {
			const numericWorkcenterId = parseInt(workcenterId);
			let activeJobs: JobRole[] = [];

			if (fetchFilterAndShiftDataStatus === "fulfilled") {
				const selectedWorkcenter = filterAllWorkcenters.find(
					(workcenter: SchedulerWorkCenter) =>
						workcenter.id === numericWorkcenterId
				);

				if (selectedWorkcenter) {
					const jobIdSet = new Set(
						selectedWorkcenter?.jobIds?.split(",").map(Number)
					);
					activeJobs = filterJobRoles.filter(
						(job: JobRole) => jobIdSet.has(job.id) && job.isActive
					);
				}
			} else {
				activeJobs = allJobRoles?.filter(
					(job: JobRole) =>
						job.workCenterIds?.includes(workcenterId) && job.isActive
				);
			}

			setFilteredJobs(activeJobs);

			if (activeJobs.length === 1 && setFieldvalue && shiftType === "jobs") {
				// setLastSelectedJobId(activeJobs[0].id);
				// setLastSelectedHexColor(activeJobs[0].hexColor);
				setFieldvalue("jobId", activeJobs[0].id);
				setFieldvalue("hexColor", activeJobs[0].hexColor);
			}

			return activeJobs;
		},
		[
			allJobRoles,
			fetchFilterAndShiftDataStatus,
			filterAllWorkcenters,
			filterJobRoles,
			shiftType,
		]
	);

	const getNumberOfStaff = useCallback(
		(
			jobRole: string | null,
			locationId: string,
			workCenterId: string | number,
			startTime: string,
			endTime: string,
			shiftSkills?: any[],
			allowPartialSignups?: boolean,
			allowedWorkerTypeIds?: number[] | null
		) => {
			if (!locationId || !startTime || !endTime || !workCenterId) return;
			if (jobRole === "" && shiftSkills?.length === 0) return;

			dispatch(
				fetchMaxWorkersByLocationJob({
					locationId,
					jobId: jobRole === "" ? null : jobRole,
					workcenterId: Number(workCenterId),
					startDateTime: startTime,
					endDateTime: endTime,
					shiftSkills,
					isPartialSignupEnabled: allowPartialSignups,
					allowedWorkerTypeIds: allowedWorkerTypeIds ?? null,
					includeWorkers:
						includeEligibleWorkers && canDirectAssign(locationId),
				})
			);
		},
		[dispatch, includeEligibleWorkers, canDirectAssign]
	);

	const handleWorkcenterChange = useCallback(
		(workcenterId: string, setFieldvalue: any) => {
			// setLastSelectedWorkcenterId(workcenterId);
			filterAndAutoSelectJobs(workcenterId, setFieldvalue);
		},
		[filterAndAutoSelectJobs]
	);

	useEffect(() => {
		// The dialog opens on a Location-Workcenter rather than asking for one
		// after the times, so the start and end time it offers are already
		// governed by that location's shift duration settings.
		const defaultWorkcenter = resolveDefaultWorkcenter(
			filterAllWorkcenters,
			appliedFilters
		);
		const defaultLocation = filterAllLocations?.find(
			(l: Location) => l.id === Number(defaultWorkcenter?.locationId)
		);
		// A location without its own Limits & Controls falls back to the
		// company-wide default, as before.
		const effectiveDefaultDurationHrs =
			defaultLocation?.defaultShiftDuration ?? defaultDurationHrs;

		const initValues = getInitValues({
			draftShiftDate,
			defaultDurationHrs: effectiveDefaultDurationHrs,
			shiftPurposeId: 0,
			weekStartDay,
		});

		if (defaultWorkcenter) {
			initValues.workCenterId = `${defaultWorkcenter.id}`;
			// Left as the raw store value rather than stringified: it reaches
			// the create payload untouched, and this is the shape the
			// dropdown's own change handler already writes.
			initValues.locationId =
				defaultWorkcenter.locationId as unknown as string;
			handleWorkcenterChange(defaultWorkcenter.id.toString(), null);
			// if only one job is available, set it as default
			const jobIds = defaultWorkcenter?.jobIds?.split(",");
			const activeJobIds = allJobRoles
				.filter((job: JobRole) => job.isActive)
				.map((job: JobRole) => job.id);
			const availableJobIds = jobIds?.filter((jobId: string) =>
				activeJobIds?.includes(parseInt(jobId))
			);
			if (availableJobIds?.length === 1 && shiftType === "jobs") {
				initValues.jobId = availableJobIds[0];
				initValues.hexColor = allJobRoles.find(
					(job: JobRole) => job.id === parseInt(availableJobIds[0])
				)?.hexColor;
			}
			// if only one skill is available, set it as default
			if (filterAllSkills.length === 1 && shiftType === "skills") {
				initValues.shiftSkills = [
					{
						skillId: filterAllSkills[0].id,
						skillName: filterAllSkills[0].name,
						skillLevelId: allSkillLevels[defaultShiftsSkillLevel].id,
						level: allSkillLevels[defaultShiftsSkillLevel].level,
						id: filterAllSkills[0].id,
						name: filterAllSkills[0].name,
					},
				];
			}
		}
		setInitialShiftDateTimeValue(initValues);
		setSelectedDatesOfTheWeek(
			configureDatesOfTheWeek(new Date(initValues.startTime), weekStartDay)
		);
	}, [
		appliedFilters,
		draftShiftDate,
		defaultDurationHrs,
		weekStartDay,
		filterAllWorkcenters,
		filterAllLocations,
		allJobRoles,
		shiftType,
		filterAllSkills,
		handleWorkcenterChange,
		allSkillLevels,
		defaultShiftsSkillLevel,
	]);

	// Trigger getNumberOfStaff when jobId or other dependencies change
	useEffect(() => {
		if (filteredJobs.length === 1 && currentLocationId) {
			const jobId = filteredJobs[0]?.id.toString();
			const locationId = currentLocationId;
			const { startTime, endTime, workCenterId, allowPartialSignups, allowedWorkerTypeIds } =
				initialShiftDateTimeValues;
			if (jobId !== lastSelectedJobId) {
				getNumberOfStaff(
					jobId,
					locationId,
					workCenterId,
					new Date(startTime).toISOString(),
					new Date(endTime).toISOString(),
					[],
					allowPartialSignups,
					allowedWorkerTypeIds
				);
				setLastSelectedJobId(jobId);
			}
		} else if (
			initialShiftDateTimeValues.shiftSkills.length > 0 &&
			currentLocationId
		) {
			const { startTime, endTime, workCenterId, allowPartialSignups, allowedWorkerTypeIds } =
				initialShiftDateTimeValues;
			getNumberOfStaff(
				null,
				currentLocationId,
				workCenterId ?? null,
				new Date(startTime).toISOString(),
				new Date(endTime).toISOString(),
				initialShiftDateTimeValues.shiftSkills,
				allowPartialSignups,
				allowedWorkerTypeIds
			);
		}
	}, [
		filteredJobs,
		getNumberOfStaff,
		initialShiftDateTimeValues,
		currentLocationId,
		lastSelectedJobId,
	]);

	const calculateHours = (
		startTime: string,
		endTime: string,
		numberOfWorkersNeeded: string
	) => {
		const { hours, minutes } = calculateShiftHoursAndMinutes(
			startTime,
			endTime,
			numberOfWorkersNeeded
		);
		setTotalHours(hours);
		setTotalMins(minutes);
	};

	const calculateTotalHoursFromWorkers = (
		formValues: FormValues,
		numberOfWorkersNeeded: string
	) => {
		const { startTime, endTime } = formValues;
		if (startTime && endTime && numberOfWorkersNeeded) {
			calculateHours(startTime, endTime, numberOfWorkersNeeded);
		}
	};

	const calculateTotalHoursFromStartTime = (
		formValues: FormValues,
		startTime: string
	) => {
		const { numberOfWorkersNeeded, endTime } = formValues;
		if (startTime && endTime && numberOfWorkersNeeded) {
			calculateHours(startTime, endTime, numberOfWorkersNeeded);
		}
	};

	const calculateTotalHoursFromEndTime = (
		formValues: FormValues,
		endTime: string
	) => {
		const { numberOfWorkersNeeded, startTime } = formValues;
		if (startTime && endTime && numberOfWorkersNeeded) {
			calculateHours(startTime, endTime, numberOfWorkersNeeded);
		}
	};

	const isDisabled = (currentDate: Date, shiftStartDate: Date) => {
		return isAfter(shiftStartDate, currentDate) || false;
	};

	return [
		{
			initialValues,
			schema,
			initialShiftDateTimeValues,
			filteredJobs,
			selectedDatesOfTheWeek,
			totalHours,
			totalMins,
			shiftType,
		},
		{
			isDisabled,
			calculateTotalHoursFromEndTime,
			calculateTotalHoursFromStartTime,
			calculateTotalHoursFromWorkers,
			handleWorkcenterChange,
			getNumberOfStaff,
			setInitialShiftDateTimeValue,
			setSelectedDatesOfTheWeek,
			setTotalHours,
			setShiftType,
			setCurrentLocationId,
		},
	] as const;
};

export default useNewShiftDialog;
